import * as yup from 'yup';
import { isCleanContent, CONTENT_POLICY_ERROR } from '../utils/contentFilter.js';

export const loginValidationSchema = yup.object().shape({
  email: yup
    .string()
    .email('Invalid email address')
    .required('Email is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters'),
});
const timeFormat = /^([01]\d|2[0-3]):([0-5]\d)$/;

export const venderProfileSchema = yup.object().shape({
  logo: yup
    .mixed()
    .optional()
    .nullable()
    .test('fileSize', 'File is too large, max size is 1MB', value => {
      if (!value || !value[0]) return true; // logo is optional
      return value[0].size <= 1048576; // 1MB
    }),

  name: yup.string().required('Vendor name is required').test('content-policy', CONTENT_POLICY_ERROR, value => isCleanContent(value)),
  username: yup.string().required('Vendor name is required').test('content-policy', CONTENT_POLICY_ERROR, value => isCleanContent(value)),
  address: yup.string().required('Street address is required'),
  postcode: yup.string().required('Postcode  is required'),
  startingTime: yup.string().required('Please add starting time'),
  endingTime: yup.string().required('Please add ending time'),
  trainingDays: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        day: yup.string().required(),
        isSelected: yup.boolean().required(),
      })
    )
    .test(
      'at-least-one-day-selected',
      'Please select at least one training day',
      days => days && days.some(day => day.isSelected)
    ),

  country: yup.string().optional(),
  countryCode: yup.string().optional(),
  countryISOCode: yup.string().optional(),
  latitude: yup.number().optional().nullable(),
  longitude: yup.number().optional().nullable(),
  location: yup.string().optional(),
  is_teaching_children: yup.string().required('Please confirm if you are teaching children'),
  dbs_certificate: yup.mixed().when('is_teaching_children', {
    is: 'yes',
    then: (schema) =>
      schema.required('DBS Certificate is required').test('file-required', 'DBS Certificate is required', (value) => {
        return value && (value.length > 0 || typeof value === 'string');
      }),
    otherwise: (schema) => schema.optional().nullable(),
  }),
});

export const venderServiceSchema = yup.object().shape({
  service: yup.string().required('Please select a service'),
  cover: yup
    .mixed()
    .required('A file is required')
    .test('fileSize', 'File is too large, max size is 1MB', value => {
      // Check if the value is a string (URL) or a file
      if (typeof value === 'string') return true; // Allow string URLs without size validation
      return value && value.size <= 1048576; // Check file size if it's a file
    }),

  descriptionEn: yup
    .string()
    .required('Description is required!')
    .min(20, 'Description must be at least 20 characters')
    .max(200, 'Description must be at most 200 characters')
    .test('content-policy', CONTENT_POLICY_ERROR, value => isCleanContent(value)),
});

export const addBatchSchema = yup.object().shape({
  service: yup.string().required('Please select a service'),
  name: yup
    .string()
    .required('Please add a batch name')
    .min(3, 'Description must be at least 3 characters')
    .max(30, 'Description must be at most 30 characters')
    .test('content-policy', CONTENT_POLICY_ERROR, value => isCleanContent(value)),
  code: yup
    .string()
    .notRequired()
    .nullable()
    .transform(value => (!value ? '' : value)),
  price_unit: yup.string().default('One time'),
  price: yup
    .number()
    .typeError('Price must be a number')
    .required('Please add price')
    .min(0, 'Price cannot be negative'),
  startingTime: yup
    .string()
    .matches(timeFormat, 'Starting time must be in the format HH:mm')
    .required('Please add starting time'),
  endingTime: yup
    .string()
    .matches(timeFormat, 'Ending time must be in the format HH:mm')
    .required('Please add ending time')
    .test(
      'is-greater',
      'Ending time must be greater than starting time',
      function (value) {
        const { startingTime } = this.parent;
        if (!startingTime || !value) return true;
        return value > startingTime;
      }
    ),
  totalCandidates: yup
    .number()
    .typeError('Candidates must be a number')
    .required('Please add number of  Candidates')
    .min(0, 'Capacity cannot be negative'),
  sessions_per_pack: yup
    .number()
    .transform((value, originalValue) => (originalValue == null || String(originalValue).trim() === '' ? undefined : value))
    .typeError('Must be a number')
    .when('price_unit', {
      is: 'Per session',
      then: schema => schema.min(1, 'At least 1 session').required('Sessions per pack is required'),
      otherwise: schema => schema.notRequired(),
    }),
  trainingDays: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        day: yup.string().required(),
        isSelected: yup.boolean().required(),
      })
    )
    .test(
      'at-least-one-day-selected',
      'Please select at least one training day',
      days => days && days.some(day => day.isSelected)
    ),
  trainers: yup
    .array()
    .of(yup.string().required('Please select a trainer'))
    .min(1, 'Please select at least one trainer'),
  hall: yup.string().required('Please select a training hall'),
  startDate: yup.date().required('Please select a start date'),
  endDate: yup.date().required('Please select an end date'),
  enableHoursLogging: yup.boolean().default(false),
  hoursPerMonth: yup.number()
    .transform((value, originalValue) => (originalValue == null || String(originalValue).trim() === '' ? undefined : value))
    .when('enableHoursLogging', {
    is: true,
    then: schema =>
      schema
        .typeError('Hours per month must be a number')
        .required('Hours per month is required when logging is enabled')
        .moreThan(0, 'Hours per month must be greater than 0'),
    otherwise: schema => schema.notRequired(),
  }),
});

export const editBatchSchema = yup.object().shape({
  name: yup
    .string()
    .required('Please add a batch name')
    .min(3, 'Description must be at least 3 characters')
    .max(30, 'Description must be at most 30 characters')
    .test('content-policy', CONTENT_POLICY_ERROR, value => isCleanContent(value)),
  code: yup
    .string()
    .notRequired()
    .nullable()
    .transform(value => (!value ? '' : value)),
  price_unit: yup.string().default('One time'),
  price: yup
    .number()
    .typeError('Price must be a number')
    .required('Please add price')
    .min(0, 'Price cannot be negative'),
  startingTime: yup
    .string()
    .matches(timeFormat, 'Starting time must be in the format HH:mm')
    .required('Please add starting time'),
  endingTime: yup
    .string()
    .matches(timeFormat, 'Ending time must be in the format HH:mm')
    .required('Please add ending time')
    .test(
      'is-greater',
      'Ending time must be greater than starting time',
      function (value) {
        const { startingTime } = this.parent;
        if (!startingTime || !value) return true;
        return value > startingTime;
      }
    ),
  totalCandidates: yup
    .number()
    .typeError('Candidates must be a number')
    .required('Please add number of  Candidates')
    .min(0, 'Capacity cannot be negative'),
  sessions_per_pack: yup
    .number()
    .transform((value, originalValue) => (originalValue == null || String(originalValue).trim() === '' ? undefined : value))
    .typeError('Must be a number')
    .when('price_unit', {
      is: 'Per session',
      then: schema => schema.min(1, 'At least 1 session').required('Sessions per pack is required'),
      otherwise: schema => schema.notRequired(),
    }),
  trainingDays: yup
    .array()
    .of(
      yup.object({
        id: yup.number().required(),
        day: yup.string().required(),
        isSelected: yup.boolean().required(),
      })
    )
    .test(
      'at-least-one-day-selected',
      'Please select at least one training day',
      days => days && days.some(day => day.isSelected)
    ),
  trainers: yup
    .array()
    .of(yup.string().required('Please select a trainer'))
    .min(1, 'Please select at least one trainer'),
  hall: yup.string().required('Please select a training hall'),
  startDate: yup.date().required('Please select a start date'),
  endDate: yup.date().required('Please select an end date'),
  enableHoursLogging: yup.boolean().default(false),
  hoursPerMonth: yup.number()
    .transform((value, originalValue) => (originalValue == null || String(originalValue).trim() === '' ? undefined : value))
    .when('enableHoursLogging', {
    is: true,
    then: schema =>
      schema
        .typeError('Hours per month must be a number')
        .required('Hours per month is required when logging is enabled')
        .moreThan(0, 'Hours per month must be greater than 0'),
    otherwise: schema => schema.notRequired(),
  }),
});

export const addTrainerSchema = yup.object().shape({
  profile_pic: yup
    .mixed()
    .required('A file is required')
    .test('fileSize', 'File is too large, max size is 1MB', value => {
      if (typeof value === 'string') return true;
      return value && value.size <= 1048576; // Check file size is <= 1MB
    }),

  first_name: yup
    .string()
    .required('First name is required!')
    .min(2, 'First name must be at least 2 characters')
    .max(20, 'First name must be at most 20 characters')
    .test('content-policy', CONTENT_POLICY_ERROR, value => isCleanContent(value)),

  last_name: yup
    .string()
    .required('Last name is required!')
    .min(2, 'Last name must be at least 2 characters')
    .max(20, 'Last name must be at most 20 characters')
    .test('content-policy', CONTENT_POLICY_ERROR, value => isCleanContent(value)),

  services: yup
    .array()
    .of(yup.string().required('Each service must be a valid string')) // Validate that each item in the array is a string
    .min(1, 'At least one service must be selected')
    .required('Services are required'),

  cost: yup
    .number()
    .typeError('Cost must be a number')
    .required('Cost is required')
    .positive('Cost must be a positive number')
    .integer('Cost must be an integer')
    .min(1, 'Cost must be at least 1'),

  weekly_hours: yup
    .number()
    .typeError('Weekly hours must be a number')
    .required('Weekly hours is required')
    .positive('Weekly hours must be a positive number')
    .min(1, 'Weekly hours must be at least 1'),

  description: yup.string().optional().test('content-policy', CONTENT_POLICY_ERROR, value => !value || isCleanContent(value)),

  training_days: yup
    .array()
    .of(
      yup.object().shape({
        id: yup.number(),
        day: yup.string(),
        isSelected: yup.boolean(),
      })
    )
    .test('at-least-one-selected', 'Please select at least one training day', value => {
      return value && value.some(d => d.isSelected);
    })
    .required('Training days are required'),
});

export const addHallSchema = yup.object().shape({
  title: yup
    .string()
    .required('Title is required!')
    .min(4, 'Title must be at least 4 characters')
    .max(50, 'Title must be at most 50 characters')
    .test('content-policy', CONTENT_POLICY_ERROR, value => isCleanContent(value)),

  size: yup
    .number()
    .typeError('Room size must be a number')
    .required('Room size is required')
    .positive('Room size must be a positive number')
    .integer('Room size must be an integer')
    .min(1, 'Room size must be at least 1'),

  totalCandidates: yup
    .number()
    .typeError('Occupancy must be a number')
    .required('Occupancy is required')
    .positive('Occupancy must be a positive number')
    .integer('Occupancy must be an integer')
    .min(1, 'Occupancy must be at least 1'),
});

export const registerSchema = yup.object().shape({
  name: yup.string().optional(),
  email: yup
    .string()
    .email('Invalid email address')
    .required('Email is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  confirm: yup
    .string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Confirm Password is required'),
  terms: yup
    .boolean()
    .oneOf([true], 'You must accept the terms and conditions'),
  country: yup.string().required('Please select your country'), // Add validation for country selection
});

export const schema = yup.object().shape({
  quote: yup
    .string()
    .max(200, 'Maximum 200 characters allowed')
    .required('Quote is required'),
  offeredPrice: yup
    .number()
    .typeError('Offered Price must be a number')
    .required('Offered Price is required'),
});
